import {
  useState,
  useEffect,
  useRef,
  useCallback,
  type ReactNode,
} from 'react';
import { ChevronDown } from 'react-feather';
import { WrenchIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { Tooltip } from '@/shared/components/Tooltip';
import { Snackbar } from '@/shared/components/Snackbar';
import FilePDFIcon from '@/assets/svg/FilePDF.svg';
import FilePNGIcon from '@/assets/svg/FilePNG.svg';
import FileJPGIcon from '@/assets/svg/FileJPG.svg';
import FileJPEGIcon from '@/assets/svg/FileJPEG.svg';
import { SHARED_MESSAGES } from '@/shared/constants/messages';
import { FACILITY_LABELS as TL } from '../../constants/translations/facilityLabels';
import { format } from 'date-fns';
import {
  listEquipment,
  type EquipmentListItem,
  type EquipmentFileItem as EquipmentFile,
} from '../../api/listEquipmentApiService';

const LIMIT = 20;

// ─── File helpers (mirror technician flow) ───────────────────────────────────

function formatFileSize(bytes: number): string {
  if (bytes >= 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
  if (bytes >= 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${bytes} B`;
}

function openFileInNewTab(url: string): void {
  window.open(url, '_blank', 'noopener,noreferrer');
}

async function downloadFileToLocal(
  url: string,
  filename: string
): Promise<void> {
  try {
    const response = await fetch(url, { mode: 'cors' });
    if (!response.ok) {
      return;
    }
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
  } catch {
    // Fallback: open in new tab if fetch fails (e.g. CORS)
    openFileInNewTab(url);
  }
}

function getFileIcon(filename: string): ReactNode {
  const ext = filename.split('.').pop()?.toLowerCase() ?? '';
  switch (ext) {
    case 'pdf':
      return <FilePDFIcon />;
    case 'png':
      return <FilePNGIcon />;
    case 'jpeg':
      return <FileJPEGIcon />;
    case 'jpg':
      return <FileJPGIcon />;
    default:
      return <FilePDFIcon />;
  }
}

// ─── FileCard ─────────────────────────────────────────────────────────────────

interface FileCardProps {
  file: EquipmentFile;
  index: number;
  onDownload: (url: string, filename: string) => void;
  testIdPrefix?: string;
}

const FileCard = ({
  file,
  index,
  onDownload,
  testIdPrefix = 'file',
}: FileCardProps) => {
  const url = file.url?.trim();
  const filename = file.filename ?? '—';
  const sizeLabel = file.fileSize ? formatFileSize(file.fileSize) : '';

  return (
    <li
      key={file.resourceId ?? index}
      className="flex items-center justify-between p-2 bg-white rounded"
    >
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <Tooltip
          content="View file"
          side="bottom"
          align="center"
          contentMaxWidth={80}
          useContentWidth
          forceShow
          triggerWrapContent
          triggerTabIndex={-1}
        >
          <button
            type="button"
            onClick={() => url && openFileInNewTab(url)}
            className="shrink-0 p-0 border-0 bg-transparent cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-color-30 rounded"
            style={{ width: '40px', height: '40px' }}
            aria-label={`Otwórz ${filename}`}
          >
            {getFileIcon(filename)}
          </button>
        </Tooltip>
        <div className="flex flex-col min-w-0 flex-1">
          <Tooltip
            content={filename}
            side="bottom"
            align="center"
            contentMaxWidth={500}
            useContentWidth
          >
            <p className="text-[16px] leading-6 tracking-[0.5px] text-[#2A4455] font-normal truncate">
              {filename}
            </p>
          </Tooltip>
          {sizeLabel && (
            <p className="text-[12px] leading-4 tracking-[0.4px] text-[#878B8E] font-normal">
              {sizeLabel}
            </p>
          )}
        </div>
      </div>
      <Tooltip
        content="Download file"
        side="bottom"
        align="center"
        contentMaxWidth={120}
        useContentWidth
        forceShow
        triggerWrapContent
        triggerTabIndex={-1}
      >
        <button
          type="button"
          data-testid={`${testIdPrefix}-download-${index}`}
          onClick={() => url && onDownload(url, filename)}
          className="shrink-0 p-1 rounded-full border-0 bg-transparent cursor-pointer text-[#474A4C] hover:bg-[#474A4C14] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-color-30 disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label={`Pobierz ${filename}`}
          disabled={!url}
        >
          <ArrowDownTrayIcon className="w-6 h-6" />
        </button>
      </Tooltip>
    </li>
  );
};

// ─── EquipmentAccordionCard ───────────────────────────────────────────────────

interface EquipmentAccordionCardProps {
  equipment: EquipmentListItem;
  index: number;
  expanded: boolean;
  onToggle: () => void;
  onDownload: (url: string, filename: string) => void;
}

const EquipmentAccordionCard = ({
  equipment,
  index,
  expanded,
  onToggle,
  onDownload,
}: EquipmentAccordionCardProps) => {
  return (
    <div
      data-testid={`equipment-list-item-${index}`}
      className="border border-[#DCDDE0] rounded-xl overflow-hidden"
    >
      <div
        className="p-4 flex items-center gap-4 cursor-pointer"
        onClick={onToggle}
      >
        <div className="w-10 h-10 rounded-full bg-neutral-90 flex items-center justify-center shrink-0">
          {equipment.image?.url ? (
            <img
              src={equipment.image.url}
              alt={equipment.name}
              className="w-10 h-10 rounded-full object-cover"
            />
          ) : (
            <WrenchIcon className="w-6 h-6 text-on-surface-variant" />
          )}
        </div>

        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <p className="text-bodyMedium text-neutral-10 wrap-break-word">
            {equipment.name}
          </p>
          <div className="flex items-center gap-1">
            <WrenchIcon className="w-4 h-4 text-outline shrink-0" />
            <span className="text-bodySmall text-outline">
              {equipment.equipmentId ?? '—'}
            </span>
          </div>
        </div>

        <button
          type="button"
          data-testid={`equipment-accordion-toggle-${index}`}
          onClick={(e) => {
            e.stopPropagation();
            onToggle();
          }}
          className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-[#474A4C1A] active:bg-[#474A4C14] cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2A4455] transition-transform duration-200"
          aria-label={`${expanded ? 'Zwiń' : 'Rozwiń'} szczegóły aparatury ${equipment.name}`}
          aria-expanded={expanded}
          style={{ transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
        >
          <ChevronDown size={24} strokeWidth={1.5} color="#474A4C" />
        </button>
      </div>

      {expanded && (
        <div className="px-4 pb-4 flex flex-col gap-4">
          <p
            data-testid="equipment-section-header"
            className="text-[#474A4C] font-normal"
            style={{ fontSize: '18px', lineHeight: '26px' }}
          >
            Dane aparatury
          </p>

          {/* Category | Model Name | Serial Number */}
          <div className="flex gap-8">
            <div className="flex-1 min-w-0 flex flex-col gap-1">
              <p
                className="text-[#474A4C]"
                style={{
                  fontSize: '12px',
                  lineHeight: '16px',
                  letterSpacing: '0.4px',
                }}
              >
                Kategoria
              </p>
              <p
                className="text-[#181A1B] break-words"
                style={{
                  fontSize: '16px',
                  lineHeight: '24px',
                  letterSpacing: '0.5px',
                }}
              >
                {equipment.categoryLabel || '—'}
              </p>
            </div>
            <div className="flex-1 min-w-0 flex flex-col gap-1">
              <p
                className="text-[#474A4C]"
                style={{
                  fontSize: '12px',
                  lineHeight: '16px',
                  letterSpacing: '0.4px',
                }}
              >
                Nazwa modelu
              </p>
              <p
                className="text-[#181A1B] break-words"
                style={{
                  fontSize: '16px',
                  lineHeight: '24px',
                  letterSpacing: '0.5px',
                }}
              >
                {equipment.modelName || '—'}
              </p>
            </div>
            <div className="flex-1 min-w-0 flex flex-col gap-1">
              <p
                className="text-[#474A4C]"
                style={{
                  fontSize: '12px',
                  lineHeight: '16px',
                  letterSpacing: '0.4px',
                }}
              >
                Numer seryjny
              </p>
              <p
                className="text-[#181A1B] break-words"
                style={{
                  fontSize: '16px',
                  lineHeight: '24px',
                  letterSpacing: '0.5px',
                }}
              >
                {equipment.serialNumber || '—'}
              </p>
            </div>
          </div>

          {/* Date of Production */}
          <div className="flex flex-col gap-1">
            <p
              className="text-[#474A4C]"
              style={{
                fontSize: '12px',
                lineHeight: '16px',
                letterSpacing: '0.4px',
              }}
            >
              Data produkcji
            </p>
            <p
              className="text-[#181A1B]"
              style={{
                fontSize: '16px',
                lineHeight: '24px',
                letterSpacing: '0.5px',
              }}
            >
              {equipment.dateOfProduction
                ? format(new Date(equipment.dateOfProduction), 'yyyy/MM/dd')
                : '—'}
            </p>
          </div>

          {/* Description */}
          <div className="flex flex-col gap-1">
            <p
              className="text-[#474A4C]"
              style={{
                fontSize: '12px',
                lineHeight: '16px',
                letterSpacing: '0.4px',
              }}
            >
              Opis
            </p>
            <p
              data-testid="equipment-description-value"
              className="text-[#181A1B] wrap-break-word"
              style={{
                fontSize: '16px',
                lineHeight: '24px',
                letterSpacing: '0.5px',
              }}
            >
              {equipment.description || '—'}
            </p>
          </div>

          {/* Equipment image */}
          {equipment.image && (
            <div className="flex flex-col gap-1">
              <p
                className="text-[#474A4C]"
                style={{
                  fontSize: '12px',
                  lineHeight: '16px',
                  letterSpacing: '0.4px',
                }}
              >
                Zdjęcie aparatury
              </p>
              <ul className="flex flex-col gap-2 list-none p-0 m-0">
                <FileCard
                  file={equipment.image}
                  index={0}
                  onDownload={onDownload}
                  testIdPrefix="equipment-image"
                />
              </ul>
            </div>
          )}

          {/* Safety documents */}
          {equipment.docs && equipment.docs.length > 0 && (
            <div className="flex flex-col gap-1">
              <p
                className="text-[#474A4C]"
                style={{
                  fontSize: '12px',
                  lineHeight: '16px',
                  letterSpacing: '0.4px',
                }}
              >
                Dokumenty bezpieczeństwa
              </p>
              <ul className="flex flex-col gap-2 list-none p-0 m-0">
                {equipment.docs.map((doc, idx) => (
                  <FileCard
                    key={doc.resourceId ?? idx}
                    file={doc}
                    index={idx}
                    onDownload={onDownload}
                    testIdPrefix="equipment-doc"
                  />
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── EquipmentAccordionList ───────────────────────────────────────────────────

interface EquipmentAccordionListProps {
  facilityId: string;
  clientId: string;
  onTotalCountChange?: (count: number) => void;
  onAccessDenied?: () => void;
}

export const EquipmentAccordionList = ({
  facilityId,
  clientId,
  onTotalCountChange,
  onAccessDenied,
}: EquipmentAccordionListProps) => {
  const [items, setItems] = useState<EquipmentListItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [showDownloadSnackbar, setShowDownloadSnackbar] = useState(false);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const loadMore = useCallback(() => {
    if (hasNextPage && !isLoading) {
      setPage((prev) => prev + 1);
    }
  }, [hasNextPage, isLoading]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) {
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          loadMore();
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [loadMore]);

  const handleDownload = (url: string, filename: string): void => {
    setShowDownloadSnackbar(true);
    void downloadFileToLocal(url, filename);
  };

  useEffect(() => {
    setItems([]);
    setPage(1);
    setHasNextPage(false);
    setExpandedId(null);
  }, [facilityId, clientId]);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);

    listEquipment({ clientId, facilityId, page, limit: LIMIT, isValid: true })
      .then((result) => {
        if (cancelled) {
          return;
        }
        if (result.accessDenied) {
          onAccessDenied?.();
          return;
        }
        if (result.success && result.data) {
          const incoming = result.data.equipment;
          setItems((prev) => (page === 1 ? incoming : [...prev, ...incoming]));
          setHasNextPage(result.data.pagination.hasNextPage);
          if (page === 1) {
            onTotalCountChange?.(result.data.pagination.totalCount);
          }
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [facilityId, clientId, page, onTotalCountChange, onAccessDenied]);

  if (isLoading && items.length === 0) {
    return (
      <div className="flex flex-col gap-3">
        {Array.from({ length: 1 }).map((_, idx) => (
          <div
            key={idx}
            className="border border-[#DCDDE0] rounded-xl px-4 py-3 flex items-center gap-3 animate-pulse"
          >
            <div className="w-9 h-9 rounded-full bg-gray-200 shrink-0" />
            <div className="flex flex-col gap-2 flex-1 min-w-0">
              <div className="h-4 bg-gray-200 rounded w-3/4" />
              <div className="h-3 bg-gray-200 rounded w-1/3" />
            </div>
            <div className="w-20 h-6 bg-gray-200 rounded-lg shrink-0" />
            <div className="w-10 h-10 rounded-full bg-gray-200 shrink-0" />
          </div>
        ))}
      </div>
    );
  }

  if (!isLoading && items.length === 0) {
    return (
      <div className="border border-[#DCDDE0] rounded-xl bg-white p-4">
        <div
          style={{
            width: '100%',
            textAlign: 'center',
            justifyContent: 'center',
            display: 'flex',
            flexDirection: 'column',
            color: '#474A4C',
            fontSize: 14,
            fontFamily: 'Roboto',
            fontWeight: '400',
            lineHeight: '20px',
            letterSpacing: 0.25,
            wordWrap: 'break-word',
          }}
        >
          {TL.EQUIPMENT_LIST_EMPTY.polish}
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col gap-3">
        {items.map((equipment, idx) => (
          <EquipmentAccordionCard
            key={equipment.id}
            equipment={equipment}
            index={idx}
            expanded={expandedId === equipment.id}
            onToggle={() =>
              setExpandedId((prev) =>
                prev === equipment.id ? null : equipment.id
              )
            }
            onDownload={handleDownload}
          />
        ))}

        {isLoading &&
          Array.from({ length: 1 }).map((_, idx) => (
            <div
              key={`shimmer-${idx}`}
              className="border border-[#DCDDE0] rounded-xl px-4 py-3 flex items-center gap-3 animate-pulse"
            >
              <div className="w-9 h-9 rounded-full bg-gray-200 shrink-0" />
              <div className="flex flex-col gap-2 flex-1 min-w-0">
                <div className="h-4 bg-gray-200 rounded w-3/4" />
                <div className="h-3 bg-gray-200 rounded w-1/3" />
              </div>
              <div className="w-20 h-6 bg-gray-200 rounded-lg shrink-0" />
              <div className="w-10 h-10 rounded-full bg-gray-200 shrink-0" />
            </div>
          ))}

        <div ref={sentinelRef} />
      </div>

      <Snackbar
        open={showDownloadSnackbar}
        message={SHARED_MESSAGES.DOWNLOAD_READY_MESSAGE}
        variant="success"
        onClose={() => setShowDownloadSnackbar(false)}
        autoHideDuration={10000}
        position="top-center"
        showCloseButton
      />
    </>
  );
};
